import { Code, ConnectError } from '@connectrpc/connect'
import { useMutation } from '@connectrpc/connect-query'
import {
  IntradayOrderWriteBlockedReason,
  IntradayOrderWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import { createBiddingIntradayOrder } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'

import { biddingQueryKeys } from '~/hooks/bidding/queryKeys'
import { useWorkflowCaseScopedMutation } from '~/hooks/bidding/useWorkflowCaseScopedMutation'
import type { InvalidatingMutationResultFor } from '~/hooks/useInvalidatingMutation'
import { readAppErrorInfo } from '~/libs/appErrorInfo'

/**
 * 手数料自動考慮の設定・単価・入札価格の判定がプレビュー時から変わって作成・下書きの編集の保存を
 * 拒否されたか
 * (AC-BIN-004b / AC-BIN-104、 #9213)。
 */
export function isFeeAdjustedPricePreviewStale(err: unknown): boolean {
  if (!(err instanceof ConnectError) || err.code !== Code.FailedPrecondition) {
    return false
  }
  const reason = readAppErrorInfo(
    err,
    IntradayOrderWriteBlockedReasonSchema,
  )?.reason
  return (
    reason === IntradayOrderWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED ||
    reason === IntradayOrderWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE
  )
}

/** フォーミュラの変数の値・計算結果がプレビュー時から変わって作成・下書きの編集の保存を拒否されたか (AC-BIN-020)。 ÷ 0 は InvalidArgument で届く。 */
export function isFormulaPricePreviewStale(err: unknown): boolean {
  if (!(err instanceof ConnectError)) return false
  const reason = readAppErrorInfo(
    err,
    IntradayOrderWriteBlockedReasonSchema,
  )?.reason
  return (
    reason === IntradayOrderWriteBlockedReason.FORMULA_VARIABLE_UNAVAILABLE ||
    reason === IntradayOrderWriteBlockedReason.FORMULA_NOT_CALCULABLE
  )
}

/**
 * 作成・下書きの保存の拒否で、 開いたままのモーダルが突き合わせているプレビューを取り直す
 * (#9213 / AC-BIN-005c / AC-BIN-020)。
 * - フォーミュラの理由・手数料の理由: フォーミュラのプレビュー (手数料の判定も持つため)
 * - 手数料の理由: 入札価格のプレビュー。 `withBidLimitMatrix` の時は入札制限の設定 (手数料自動考慮の
 *   ON/OFF) も取り直す
 * それ以外の拒否では何も取り直さない (undefined)。
 */
export function invalidateIntradayPreviewsOnRejection(
  queryClient: QueryClient,
  err: unknown,
  { withBidLimitMatrix }: { withBidLimitMatrix: boolean },
): Promise<unknown> | undefined {
  const feeStale = isFeeAdjustedPricePreviewStale(err)
  if (!feeStale && !isFormulaPricePreviewStale(err)) return undefined
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: biddingQueryKeys.intradayFormulaPricePreview,
    }),
    ...(feeStale
      ? [
          queryClient.invalidateQueries({
            queryKey: biddingQueryKeys.intradayFeeAdjustedPricePreview,
          }),
          ...(withBidLimitMatrix
            ? [
                queryClient.invalidateQueries({
                  queryKey: biddingQueryKeys.bidLimitMatrix,
                }),
              ]
            : []),
        ]
      : []),
  ])
}

/**
 * 時間前注文 新規作成 mutation hook。
 * onSuccess で list query と板発注のコマリボンの件数 (AC-BIN-025) を invalidate して
 * 最新化する。 手数料自動考慮の変化で
 * 拒否された時は、 開いたままの札作成モーダルが突き合わせている 2 つ (入札価格のプレビューと、
 * 入札制限の設定の ON/OFF) を両方取り直す (#9213)。 プレビューだけを取り直すと、 ON/OFF が
 * 切り替わった後の応答が古い設定と食い違い、 モーダルが再読み込みの案内で行き止まる。
 */
export function useCreateBiddingIntradayOrder(): InvalidatingMutationResultFor<
  typeof createBiddingIntradayOrder
> {
  const queryClient = useQueryClient()
  return useWorkflowCaseScopedMutation(
    useMutation(createBiddingIntradayOrder, {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: biddingQueryKeys.intradayList,
          }),
          queryClient.invalidateQueries({
            queryKey: biddingQueryKeys.intradayActiveOrderCounts,
          }),
        ]),
      onError: (err) =>
        invalidateIntradayPreviewsOnRejection(queryClient, err, {
          withBidLimitMatrix: true,
        }),
    }),
  )
}
